import $ from 'jquery'
import Interact from 'interactjs'
import { Store } from 'vuex'
import RootState from '../../store/state'
import { CONTENTS_CONTAINER_SELECTOR } from '../constants/ContainerSelector'
import { Actions, Mutations } from '../../store/editor/core/helpers'
import * as ComponentsHelpers from '../../store/editor/components/helpers'

export interface DraggableClasses {
  dragging?: string
  draggingGlobal?: string
}

export const DRAG_EVENT_CLASS_PREFIX = 'es-drag-position--'

export const EventType = {
  DEFAULT: 'default',
  SECTION: 'section'
}

export interface DraggableOption {
  /** ドラッグするコンポーネントを格納するコンテナ */
  container?: HTMLElement | string
  /** ドラッグ開始を受け付けるポイント */
  handle?: HTMLElement | string
  /** ドラッグ時に使用するクラス定義 */
  classes?: DraggableClasses
  /** ドラッグ時、 CSS transform に指定する値 */
  extraTransform?: string,
  /** ドラッグイベントの種類 */
  eventType?: string
}

interface ScrollPosition {
  left: number
  top: number
}

const DEFAULT_OPTION: DraggableOption = {
  container: CONTENTS_CONTAINER_SELECTOR,
  eventType: EventType.DEFAULT,
  classes: {
    dragging: 'drag-event--dragging',
    draggingGlobal: 'disable-tooltip'
  }
}

export default class Draggable {
  private option: DraggableOption
  private scrollOnDragStart: ScrollPosition

  constructor(
    private readonly element: HTMLElement | string,
    private readonly interactable: Interact.Interactable,
    private store: Store<RootState>,
    option?: DraggableOption) {
    this.option = Object.assign({}, DEFAULT_OPTION, option || {})
    this.init()
  }

  private get container(): HTMLElement | string {
    return this.option.container || document.body
  }

  private get handle(): HTMLElement | string | undefined {
    return this.option.handle
  }

  private get classes(): DraggableClasses {
    return this.option.classes || {}
  }

  private get extraTransform(): string {
    return this.option.extraTransform || ''
  }

  private get eventType(): string {
    return this.option.eventType || EventType.DEFAULT
  }

  private get eventTypeAsClass(): string {
    return DRAG_EVENT_CLASS_PREFIX + this.eventType
  }

  public on(eventType: string, listener: (event: Interact.InteractEvent) => boolean | undefined, useCapture?: boolean): this {
    this.interactable.on(eventType, listener, useCapture)
    return this
  }

  public off(eventType: string, listener: (event: Interact.InteractEvent) => boolean | undefined, useCapture?: boolean): this {
    this.interactable.off(eventType, listener, useCapture)
    return this
  }

  private init(): void {
    // ドラッグ中にスクロール位置が変化したときでも、マウスの位置を正しく計算させる
    Interact.dynamicDrop(true)

    this.interactable
      .set({ allowFrom: this.handle })
      .draggable({
        autoScroll: {
          container: $(this.container).get(0),
          speed: 2000 // the scroll speed in pixels per second
        }
      })
      .on('dragstart', (event: Interact.InteractEvent) => {
        this.scrollOnDragStart = this.getContainerScrollPosition()
        $(this.element)
          .addClass(this.eventTypeAsClass)
          .addClass(this.classes.dragging || '')
        $(document.documentElement).addClass(this.classes.draggingGlobal || '')
        Actions.setDragEventType(this.store, this.eventType)
        ComponentsHelpers.Actions.requestSaveState(this.store)
        Mutations.clearFocus(this.store)
      })
      .on('dragmove', (event: Interact.InteractEvent) => {
        const scrollOffset = this.getScrollOffset()
        const x = event.pageX - event.x0 + scrollOffset.left
        const y = event.pageY - event.y0 + scrollOffset.top
        this.movePosition(x, y)
      })
      .on('dragend', (event: Interact.InteractEvent) => {
        this.resetPosition()
        $(this.element)
          .removeClass(this.eventTypeAsClass)
          .removeClass(this.classes.dragging || '')
        $(document.documentElement).removeClass(this.classes.draggingGlobal || '')
        Actions.hideDropPoint(this.store)
      })
      .on('up', () => {
        // interact.js のバグを回避する。（バージョン 1.2.9 時点で発生していることを確認）
        // ドラッグ地点を単にクリックしたとき、 html 要素に style="cursor: pointer;" が設定されてしまい、戻らない不具合がある。
        // 仕方ないので自前でスタイルを戻す。
        document.documentElement.style.cursor = ''
      })
  }

  private getContainerScrollPosition(): ScrollPosition {
    const container = $(this.container)
    return {
      left: container.scrollLeft() || 0,
      top: container.scrollTop() || 0
    }
  }

  private getScrollOffset(): ScrollPosition {
    const currentScroll = this.getContainerScrollPosition()
    return {
      left: currentScroll.left - this.scrollOnDragStart.left,
      top: currentScroll.top - this.scrollOnDragStart.top
    }
  }

  private movePosition(x: number, y: number): void {
    const translate = 'translate(' + x + 'px,' + y + 'px) ' + this.extraTransform
    $(this.element).css('transform', translate)
  }

  private resetPosition(): void {
    $(this.element).css('transform', '')
  }
}
